"use client";

import { useState } from "react";
import { ModalBase } from "../Modals/ModalBase";
import { DateRangeButton, type DateRangeButtonProps } from "../DateRangeButton";
import DateRangePicker, { type DateRangePickerProps, type DateRangeValue } from "../DateRangePicker";

export interface DateRangePickerFieldProps
  extends Omit<DateRangePickerProps, "closeOnApply">,
    Pick<DateRangeButtonProps, "placeholder"> {
  /** Valor controlado */
  value: DateRangeValue;
  /** Classes CSS adicionais para o botão */
  className?: string;
}

/**
 * Componente completo de seleção de período com botão e modal integrados.
 * Encapsula DateRangeButton + ModalBase + DateRangePicker.
 *
 * @example
 * ```tsx
 * const [dateRange, setDateRange] = useState<DateRangeValue>({
 *   startDate: null,
 *   endDate: null,
 * });
 *
 * <DateRangePickerField
 *   value={dateRange}
 *   onChange={setDateRange}
 *   placeholder="Selecione o período"
 *   className="ml-auto"
 * />
 * ```
 */
export function DateRangePickerField({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  ...pickerProps
}: DateRangePickerFieldProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleChange = (range: DateRangeValue) => {
    onChange(range);
    setIsModalOpen(false);
  };

  return (
    <>
      <DateRangeButton
        value={value}
        onClick={() => setIsModalOpen(true)}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
      />

      <ModalBase isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-lg:px-1 max-lg:max-w-[90%]">
        <div className="lg:-mx-4 -my-2">
          <DateRangePicker value={value} onChange={handleChange} disabled={disabled} {...pickerProps} />
        </div>
      </ModalBase>
    </>
  );
}
